import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { setUserContext, clearUserContext, logBreadcrumb, logError } from '../utils/logger';
import { identifyUser, resetAnalytics } from '../utils/analytics';

const AuthContext = createContext({});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const STORAGE_KEY = 'morse_auth_session';

    useEffect(() => {
        // 1. Restore session from storage
        const restoreSession = async () => {
            const localSession = localStorage.getItem(STORAGE_KEY);
            const sessionSession = sessionStorage.getItem(STORAGE_KEY);
            const storedSession = localSession || sessionSession;

            if (storedSession) {
                try {
                    const session = JSON.parse(storedSession);
                    const { error } = await supabase.auth.setSession(session);
                    if (error) throw error;
                } catch (err) {
                    console.error('Error restoring session:', err);
                    localStorage.removeItem(STORAGE_KEY);
                    sessionStorage.removeItem(STORAGE_KEY);
                }
            }

            // If no session restored, getSession will return null, which is fine.
            const { data: { session } } = await supabase.auth.getSession();
            setUser(session?.user ?? null);
            setLoading(false);
        };

        restoreSession();

        // 2. Listen for auth changes and persist/clear session
        const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
            setUser(session?.user ?? null);
            setLoading(false);

            if (event === 'SIGNED_OUT') {
                localStorage.removeItem(STORAGE_KEY);
                sessionStorage.removeItem(STORAGE_KEY);
                resetAnalytics(); // Clear tracking
            } else if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED' || event === 'USER_UPDATED') {
                if (session) {
                    identifyUser(session.user.id, { email: session.user.email }); // Identify user
                    // Update whichever storage is currently in use
                    // Update whichever storage is currently in use, or default to localStorage for fresh logins (e.g. email links)
                    if (sessionStorage.getItem(STORAGE_KEY)) {
                        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session));
                    } else {
                        localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
                    }
                }
            }
        });

        return () => subscription.unsubscribe();
    }, []);

    const signUp = async (email, password, data = {}) => {
        try {
            const result = await supabase.auth.signUp({
                email,
                password,
                options: { data }
            });

            if (result.error) {
                console.error('SignUp Error:', result.error);
                logError(result.error, { function: 'signUp', email: email.split('@')[0] });
            } else {
                logBreadcrumb('User signed up', 'auth', { email: email.split('@')[0] });
            }

            return result;
        } catch (e) {
            console.error('SignUp Exception:', e);
            logError(e, { function: 'signUp', email: email.split('@')[0] });
            return { error: e };
        }
    };

    const signIn = async (email, password, rememberMe = true) => {
        try {
            const result = await supabase.auth.signInWithPassword({ email, password });

            if (result.data?.session) {
                const storage = rememberMe ? localStorage : sessionStorage;
                // Clear other storage to avoid confusion
                if (rememberMe) sessionStorage.removeItem(STORAGE_KEY);
                else localStorage.removeItem(STORAGE_KEY);

                storage.setItem(STORAGE_KEY, JSON.stringify(result.data.session));

                // Set user context in Sentry (privacy-safe: no email)
                const username = email.split('@')[0]; // Just username, no domain
                setUserContext(result.data.session.user.id, username);
                logBreadcrumb('User signed in', 'auth', { rememberMe });
            }

            if (result.error) {
                console.error('SignIn Error:', result.error);
                logError(result.error, { function: 'signIn', email: email.split('@')[0] });
            }
            return result;
        } catch (e) {
            console.error('SignIn Exception:', e);
            logError(e, { function: 'signIn', email: email.split('@')[0] });
            return { error: e };
        }
    };

    const signOut = async () => {
        logBreadcrumb('User signed out', 'auth');
        clearUserContext(); // Clear Sentry user context
        localStorage.removeItem(STORAGE_KEY);
        sessionStorage.removeItem(STORAGE_KEY);
        return supabase.auth.signOut();
    };

    const resetPassword = async (email) => {
        try {
            const result = await supabase.auth.resetPasswordForEmail(email, {
                redirectTo: `${window.location.origin}/reset-password`,
            });
            if (result.error) console.error('Reset Password Error:', result.error);
            return result;
        } catch (e) {
            console.error('Reset Password Exception:', e);
            return { error: e };
        }
    };

    const updatePassword = async (newPassword) => {
        try {
            const result = await supabase.auth.updateUser({ password: newPassword });
            if (result.error) console.error('Update Password Error:', result.error);
            return result;
        } catch (e) {
            console.error('Update Password Exception:', e);
            return { error: e };
        }
    };

    const signInWithOAuth = async (provider) => {
        try {
            const { data, error } = await supabase.auth.signInWithOAuth({
                provider,
                options: {
                    redirectTo: window.location.origin,
                },
            });
            if (error) throw error;
            return { data, error: null };
        } catch (e) {
            console.error(`${provider} Sign-In Exception:`, e);
            logError(e, { function: 'signInWithOAuth', provider });
            return { error: e };
        }
    };

    const value = {
        user,
        signUp,
        signIn,
        signOut,
        resetPassword,
        updatePassword,
        signInWithOAuth,
        loading
    };

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    );
};
